<template>
    <div class="hour-checkboxes">
        <div class="hour-row">
            <div class="date-info">{{ date }}</div>
            <div class="hour-grid-container">
                <!-- Подсказка с часами -->
                <div class="hour-labels">
                    <div v-for="hour in 24" :key="`label-${hour}`" class="hour-label">
                        {{ (hour - 1).toString().padStart(2, '0') }}
                    </div>
                </div>
                <!-- Чекбоксы -->
                <div class="hour-grid">
                    <div 
                        v-for="hour in 24" 
                        :key="hour"
                        class="hour-checkbox"
                        :class="{ active: isHourActive(hour) }"
                        @click="toggleHour(hour)"
                        :title="`${formatHourRange(hour)} - ${isHourActive(hour) ? 'активен' : 'неактивен'}`"
                    >
                        <div class="checkbox-square">
                            <div class="square-fill"></div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="time-summary">
                <div class="total-hours">{{ getActiveHoursCount() }}ч</div>
                <div class="total-cost">{{ formatPrice(getTotalCost()) }}</div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'HourCheckboxes',
    props: {
        date: {
            type: String,
            required: true
        },
        timeSettings: {
            type: Array,
            default: () => []
        },
        hourlyPrice: {
            type: Number,
            default: 0
        },
        checkboxStyle: {
            type: Number,
            default: 1,
            validator: value => [1, 2, 3, 4].includes(value)
        }
    },
    emits: ['update:timeSettings'],
    data() {
        return {
            activeHours: new Set()
        }
    },
    mounted() {
        this.initializeActiveHours();
    },
    watch: {
        timeSettings: {
            handler(newSettings) {
                this.updateActiveHoursFromTimeSettings();
            },
            deep: true,
            immediate: true
        }
    },
    methods: {
        initializeActiveHours() {
            // Если есть timeSettings, используем их, иначе все часы активны
            if (this.timeSettings && this.timeSettings.length > 0) {
                this.updateActiveHoursFromTimeSettings();
            } else {
                // По умолчанию все часы активны
                for (let i = 1; i <= 24; i++) {
                    this.activeHours.add(i);
                }
            }
        },
        updateActiveHoursFromTimeSettings() {
            this.activeHours.clear();
            
            if (!this.timeSettings || this.timeSettings.length === 0) {
                return;
            }
            
            // Конвертируем timeSettings в активные часы
            this.timeSettings.forEach(ts => {
                const startHour = Math.floor(ts.startTime / (60 * 60 * 1000)) + 1; // +1 для соответствия индексации 1-24
                const endHour = Math.floor(ts.endTime / (60 * 60 * 1000)) + 1;
                
                for (let hour = startHour; hour < endHour; hour++) {
                    this.activeHours.add(hour);
                }
            });
        },
        isHourActive(hour) {
            return this.activeHours.has(hour);
        },
        toggleHour(hour) {
            if (this.activeHours.has(hour)) {
                this.activeHours.delete(hour);
            } else {
                this.activeHours.add(hour);
            }
            
            const timeSettings = this.getTimeSettings();

            this.$emit('update:timeSettings', timeSettings);
        },
        getActiveHoursCount() {
            return this.activeHours.size;
        },
        getTotalCost() {
            return this.activeHours.size * this.hourlyPrice;
        },
        getTimeSettings() {
            // Конвертируем активные часы в настройки времени согласно API
            const settings = [];
            let currentStart = null;
            let currentEnd = null;
            
            for (let hour = 1; hour <= 25; hour++) {
                const isActive = this.activeHours.has(hour);
                
                if (isActive && currentStart === null) {
                    // Начало нового периода
                    currentStart = (hour - 1) * 60 * 60 * 1000; // в миллисекундах от начала дня
                } else if (!isActive && currentStart !== null) {
                    // Конец периода
                    currentEnd = (hour - 1) * 60 * 60 * 1000;
                    settings.push({
                        startTime: currentStart,
                        endTime: currentEnd
                    });
                    currentStart = null;
                    currentEnd = null;
                }
            }
            
            // Если последний период доходит до конца дня (24:00)
            if (currentStart !== null) {
                settings.push({
                    startTime: currentStart,
                    endTime: 24 * 60 * 60 * 1000 // 24 часа в миллисекундах
                });
            }
            
            return settings;
        },
        formatPrice(amount) {
            const num = Number(amount || 0);
            return new Intl.NumberFormat('ru-RU', {
                style: 'currency',
                currency: 'KZT',
                currencyDisplay: 'symbol',
                maximumFractionDigits: 2,
            }).format(num);
        },
        formatHourRange(hour) {
            const startHour = hour - 1;
            const endHour = hour;
            return `${startHour.toString().padStart(2, '0')}:00 - ${endHour.toString().padStart(2, '0')}:00`;
        }
    }
}
</script>

<style scoped>
.hour-checkboxes {
    margin-bottom: 12px;
}

.hour-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid #f1f5f9;
}

.date-info {
    min-width: 80px;
    font-size: 13px;
    font-weight: 500;
    color: #64748b;
}

.hour-grid-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    align-items: center;
}

.hour-labels {
    display: flex;
    gap: 2px;
    margin-bottom: 4px;
}

.hour-label {
    width: 17px;
    text-align: center;
    font-size: 9px;
    color: #94a3b8;
    font-weight: 500;
}

.hour-grid {
    display: flex;
    gap: 2px;
    justify-content: center;
}

.hour-checkbox {
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
}

.hour-checkbox:hover {
    transform: scale(1.1);
}

/* Стиль 1: Круглые чекбоксы */
.style-1 .hour-checkbox {
    width: 12px !important;
    height: 12px !important;
}

.style-1 .checkbox-circle {
    width: 100% !important;
    height: 100% !important;
    border: 2px solid #cbd5e1 !important;
    border-radius: 50% !important;
    position: relative !important;
    transition: all 0.2s ease !important;
}

.style-1 .checkbox-inner {
    width: 6px !important;
    height: 6px !important;
    background: transparent !important;
    border-radius: 50% !important;
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    transition: all 0.2s ease !important;
}

.style-1 .hour-checkbox.active .checkbox-circle {
    border-color: #3b82f6 !important;
    background: #dbeafe !important;
}

.style-1 .hour-checkbox.active .checkbox-inner {
    background: #3b82f6 !important;
}

/* Стиль 2: Полоски времени */
.style-2 .hour-checkbox {
    width: 8px !important;
    height: 16px !important;
}

.style-2 .time-bar {
    width: 100% !important;
    height: 100% !important;
    background: #e2e8f0 !important;
    border-radius: 2px !important;
    position: relative !important;
    transition: all 0.2s ease !important;
}

.style-2 .bar-fill {
    width: 100% !important;
    height: 0% !important;
    background: linear-gradient(135deg, #3b82f6, #1d4ed8) !important;
    border-radius: 2px !important;
    transition: all 0.3s ease !important;
}

.style-2 .hour-checkbox.active .bar-fill {
    height: 100% !important;
}

.style-2 .hour-checkbox:hover .time-bar {
    background: #cbd5e1 !important;
}

/* Зеленые квадраты */
.hour-checkbox {
    width: 17px !important;
    height: 17px !important;
}

.checkbox-square {
    width: 100% !important;
    height: 100% !important;
    border: 1px solid #cbd5e1 !important;
    border-radius: 3px !important;
    position: relative !important;
    transition: all 0.2s ease !important;
}

.square-fill {
    width: 0% !important;
    height: 0% !important;
    background: #10b981 !important;
    border-radius: 2px !important;
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    transition: all 0.2s ease !important;
}

.hour-checkbox.active .checkbox-square {
    border-color: #10b981 !important;
    background: #d1fae5 !important;
}

.hour-checkbox.active .square-fill {
    width: 100% !important;
    height: 100% !important;
}

/* Стиль 4: Точечные индикаторы */
.style-4 .hour-checkbox {
    width: 8px !important;
    height: 8px !important;
}

.style-4 .dot-indicator {
    width: 100% !important;
    height: 100% !important;
    position: relative !important;
}

.style-4 .dot {
    width: 4px !important;
    height: 4px !important;
    background: #94a3b8 !important;
    border-radius: 50% !important;
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    transition: all 0.2s ease !important;
}

.style-4 .hour-checkbox.active .dot {
    background: #f59e0b !important;
    width: 6px !important;
    height: 6px !important;
    box-shadow: 0 0 8px rgba(245, 158, 11, 0.4) !important;
}

.style-4 .hour-checkbox:hover .dot {
    transform: translate(-50%, -50%) scale(1.2) !important;
}

.time-summary {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    min-width: 80px;
}

.total-hours {
    font-size: 12px;
    font-weight: 600;
    color: #475569;
}

.total-cost {
    font-size: 11px;
    font-weight: 700;
    color: #059669;
}

/* Адаптивность */
@media (max-width: 768px) {
    .hour-row {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }
    
    .hour-grid {
        width: 100%;
        justify-content: space-between;
    }
    
    .time-summary {
        align-items: flex-start;
        flex-direction: row;
        gap: 8px;
    }
}
</style>
